import type { IconType } from "react-icons";
import {
  PiArrowCounterClockwiseDuotone,
  PiArrowsClockwiseDuotone,
  PiCopyDuotone,
  PiCreditCardDuotone,
  PiCurrencyInrDuotone,
  PiEnvelopeSimpleDuotone,
  PiFileXDuotone,
  PiFlagDuotone,
  PiHandPalmDuotone,
  PiHandshakeDuotone,
  PiInfoDuotone,
  PiPackageDuotone,
  PiPlugsDuotone,
  PiProhibitDuotone,
  PiReceiptDuotone,
  PiSealCheckDuotone,
  PiShieldCheckDuotone,
  PiShoppingCartDuotone,
  PiTagDuotone,
  PiUserPlusDuotone,
  PiUsersDuotone,
  PiWarningDuotone,
  PiWarningOctagonDuotone,
  PiWifiSlashDuotone,
  PiXCircleDuotone,
} from "react-icons/pi";
import type { NotificationSeverity } from "../providers/notifications";
import { cn } from "./cn";

/**
 * A back-office notice's icon (2026-09-28, the project owner's request: "show the proper icon on
 * the related message"). The SHAPE says what happened — an order waiting for approval, a refund
 * refused, the Tally agent gone quiet — read from the notice's own `kind`, the dotted event name
 * every service posts (backend `specs/002-identity-authentication/contracts/notifications.md`). The
 * COLOUR says how loud it is, from its `severity`. The Marketplace admin portal's dropdown keys its
 * icons by event type the same way.
 *
 * Every kind the backend raises today has its own entry; a kind added later falls back to its area
 * (`order.`, `tally.`, …), and anything else to the severity's own icon — never a blank tile.
 */
const BY_KIND: Record<string, IconType> = {
  "order.awaiting_approval": PiSealCheckDuotone,
  "order.credit_approval_requested": PiSealCheckDuotone,
  "order.cancelled_by_customer": PiXCircleDuotone,
  "order.short_fulfilment": PiPackageDuotone,
  "order.refund_undelivered": PiArrowCounterClockwiseDuotone,
  "order.payment_after_cancellation": PiCurrencyInrDuotone,
  "order.duplicate_payment": PiCopyDuotone,
  "customer.registered": PiUserPlusDuotone,
  "customer.terms_requested": PiHandshakeDuotone,
  "customer.statements_failed": PiEnvelopeSimpleDuotone,
  "pricing.stale_critical": PiTagDuotone,
  "packing.review_flagged": PiFlagDuotone,
  "packing.supervisor_override": PiShieldCheckDuotone,
  "packing.sync_conflict": PiArrowsClockwiseDuotone,
  "invoice.registration_failed": PiFileXDuotone,
  "invoice.registration_exhausted": PiFileXDuotone,
  "payment.referred": PiCreditCardDuotone,
  "refund.refused": PiProhibitDuotone,
  "refund.queued": PiArrowCounterClockwiseDuotone,
  "tally.sync_failed": PiPlugsDuotone,
  "tally.agent_silent": PiWifiSlashDuotone,
  "tally.dedupe_collision": PiUsersDuotone,
  "tally.receipt_held": PiHandPalmDuotone,
};

const BY_AREA: Record<string, IconType> = {
  order: PiShoppingCartDuotone,
  customer: PiUsersDuotone,
  pricing: PiTagDuotone,
  packing: PiPackageDuotone,
  invoice: PiReceiptDuotone,
  payment: PiCreditCardDuotone,
  refund: PiArrowCounterClockwiseDuotone,
  tally: PiPlugsDuotone,
};

const BY_SEVERITY: Record<NotificationSeverity, { icon: IconType; tile: string; label: string }> = {
  info: { icon: PiInfoDuotone, tile: "bg-blue-lighter/70 text-blue-dark", label: "Information" },
  warning: { icon: PiWarningDuotone, tile: "bg-orange-lighter/70 text-orange-dark", label: "Needs attention" },
  critical: { icon: PiWarningOctagonDuotone, tile: "bg-red-lighter/70 text-red-dark", label: "Critical" },
};

export function notificationIcon(kind: string): IconType | undefined {
  return BY_KIND[kind] ?? BY_AREA[kind.split(".")[0] ?? ""];
}

/** The icon in a tile tinted by severity; `label` names the severity for a screen reader. */
export function NotificationIcon({ kind, severity, className }: { kind: string; severity: NotificationSeverity; className?: string }) {
  const tone = BY_SEVERITY[severity];
  const Icon = notificationIcon(kind) ?? tone.icon;
  return (
    <span role="img" aria-label={tone.label} className={cn("flex size-9 shrink-0 items-center justify-center rounded-lg", tone.tile, className)}>
      <Icon aria-hidden className="size-5" />
    </span>
  );
}
